import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import "./animatedTabPanel.css";

function AnimatedTabPanel({ tabKey, children, className = "", contentClassName = "" }) {
  const panelRef = useRef(null);
  const latestChildrenRef = useRef(children);
  const latestContentClassNameRef = useRef(contentClassName);
  const animationFrameRef = useRef([]);
  const animationTimeoutRef = useRef(null);
  const [renderedTabKey, setRenderedTabKey] = useState(tabKey);
  const [renderedChildren, setRenderedChildren] = useState(children);
  const [renderedContentClassName, setRenderedContentClassName] = useState(contentClassName);
  const [height, setHeight] = useState("auto");
  const [isAnimating, setIsAnimating] = useState(false);

  latestChildrenRef.current = children;
  latestContentClassNameRef.current = contentClassName;

  useLayoutEffect(() => {
    if (renderedTabKey === tabKey) return undefined;

    const panel = panelRef.current;
    if (!panel) {
      setRenderedTabKey(tabKey);
      setRenderedChildren(latestChildrenRef.current);
      setRenderedContentClassName(latestContentClassNameRef.current);
      return undefined;
    }

    animationFrameRef.current.forEach((frameId) => cancelAnimationFrame(frameId));
    animationFrameRef.current = [];
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
      animationTimeoutRef.current = null;
    }

    setHeight(panel.getBoundingClientRect().height);
    setIsAnimating(true);

    const firstFrame = requestAnimationFrame(() => {
      setRenderedTabKey(tabKey);
      setRenderedChildren(latestChildrenRef.current);
      setRenderedContentClassName(latestContentClassNameRef.current);

      const secondFrame = requestAnimationFrame(() => {
        setHeight(panel.scrollHeight);
      });
      animationFrameRef.current.push(secondFrame);
    });
    animationFrameRef.current.push(firstFrame);

    return () => {
      animationFrameRef.current.forEach((frameId) => cancelAnimationFrame(frameId));
      animationFrameRef.current = [];
    };
  }, [renderedTabKey, tabKey]);

  useEffect(() => {
    if (!isAnimating) return undefined;
    if (animationTimeoutRef.current) clearTimeout(animationTimeoutRef.current);
    animationTimeoutRef.current = setTimeout(() => {
      setHeight("auto");
      setIsAnimating(false);
      animationTimeoutRef.current = null;
    }, 360);

    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
        animationTimeoutRef.current = null;
      }
    };
  }, [isAnimating]);

  useEffect(() => {
    return () => {
      animationFrameRef.current.forEach((frameId) => cancelAnimationFrame(frameId));
      if (animationTimeoutRef.current) clearTimeout(animationTimeoutRef.current);
    };
  }, []);

  const displayedChildren = renderedTabKey === tabKey && !isAnimating ? children : renderedChildren;

  return (
    <div
      ref={panelRef}
      className={`${className} animated-tab-panel ${isAnimating ? "is-animating" : ""}`.trim()}
      style={{ height }}
      onTransitionEnd={(event) => {
        if (event.target !== panelRef.current || event.propertyName !== "height") return;
        if (animationTimeoutRef.current) {
          clearTimeout(animationTimeoutRef.current);
          animationTimeoutRef.current = null;
        }
        setHeight("auto");
        setIsAnimating(false);
      }}
    >
      <div className={`animated-tab-panel__content ${renderedContentClassName}`.trim()} key={renderedTabKey}>
        {displayedChildren}
      </div>
    </div>
  );
}

export default AnimatedTabPanel;
